import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  BellRing,
  Check,
  Mail,
  Plus,
  RotateCcw,
  Save,
  Smartphone,
  Trash2,
} from 'lucide-react'

import { Button } from '../../ui/Button/Button'
import { Card } from '../../ui/Card/Card'
import { Spinner } from '../../ui/Spinner/Spinner'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import {
  notificationSettingsPayloadSchema,
  type NotificationRecipientPayload,
  type NotificationSettingsResponse,
} from './notification-settings-contracts'
import {
  isNotificationSettingsPermissionError,
  type NotificationSettingsRepository,
} from './notification-settings-repository'
import styles from './SettingsPage.module.css'

type RecipientDraft = NotificationRecipientPayload & { draftKey: string }
type RecipientDraftErrors = Partial<
  Record<keyof NotificationRecipientPayload, string>
>
type PendingRecipientDeletion = {
  draftKey: string
  recipientId: string
  recipientName: string
}
const EVENT_PREFERENCES = [
  ['appointmentBooked', 'appointmentBookedSms', 'Appointment booked'],
  ['callbackRequested', 'callbackRequestedSms', 'Callback requested'],
  [
    'cancellationRequested',
    'cancellationRequestedSms',
    'Cancellation assistance requested',
  ],
  [
    'rescheduleRequested',
    'rescheduleRequestedSms',
    'Reschedule assistance requested',
  ],
  [
    'needsAttention',
    'needsAttentionSms',
    'Needs attention / appointment request',
  ],
] as const

function draftsFromResponse(
  response: NotificationSettingsResponse
): RecipientDraft[] {
  return response.recipients.map((recipient) => ({
    id: recipient.id,
    name: recipient.name,
    email: recipient.email,
    phone: recipient.phone,
    enabled: recipient.enabled ?? recipient.emailActive,
    emailActive: recipient.emailActive,
    smsActive: recipient.smsActive,
    appointmentBooked: recipient.appointmentBooked,
    callbackRequested: recipient.callbackRequested ?? false,
    cancellationRequested: recipient.cancellationRequested ?? false,
    rescheduleRequested: recipient.rescheduleRequested ?? false,
    needsAttention: recipient.needsAttention ?? false,
    appointmentBookedSms: recipient.appointmentBookedSms,
    callbackRequestedSms: recipient.callbackRequestedSms,
    cancellationRequestedSms: recipient.cancellationRequestedSms,
    rescheduleRequestedSms: recipient.rescheduleRequestedSms,
    needsAttentionSms: recipient.needsAttentionSms,
    draftKey: recipient.id,
  }))
}

function payloadFromDrafts(drafts: RecipientDraft[]) {
  return {
    recipients: drafts.map((recipient) => ({
      id: recipient.id,
      name: recipient.name.trim(),
      email: recipient.email?.trim() || null,
      phone: recipient.phone?.trim() || null,
      enabled: recipient.enabled,
      emailActive: recipient.emailActive,
      smsActive: recipient.smsActive,
      appointmentBooked: recipient.appointmentBooked,
      callbackRequested: recipient.callbackRequested,
      cancellationRequested: recipient.cancellationRequested,
      rescheduleRequested: recipient.rescheduleRequested,
      needsAttention: recipient.needsAttention,
      appointmentBookedSms: recipient.appointmentBookedSms,
      callbackRequestedSms: recipient.callbackRequestedSms,
      cancellationRequestedSms: recipient.cancellationRequestedSms,
      rescheduleRequestedSms: recipient.rescheduleRequestedSms,
      needsAttentionSms: recipient.needsAttentionSms,
    })),
  }
}

export function NotificationSettings({
  tenantId,
  tenantLabel,
  repository,
}: {
  tenantId: string
  tenantLabel: string
  repository: NotificationSettingsRepository
}) {
  const nextDraft = useRef(0)
  const activeTenant = useRef(tenantId)
  activeTenant.current = tenantId
  const settingsQuery = useQuery({
    queryKey: ['settings', tenantId, 'notification-settings'],
    queryFn: () => repository.getSettings(tenantId),
  })
  const [baseline, setBaseline] = useState<NotificationSettingsResponse | null>(
    null
  )
  const [drafts, setDrafts] = useState<RecipientDraft[]>([])
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>(
    'idle'
  )
  const [pendingDeletion, setPendingDeletion] =
    useState<PendingRecipientDeletion | null>(null)
  const [deletionErrorRecipientId, setDeletionErrorRecipientId] = useState<
    string | null
  >(null)

  useEffect(() => {
    setBaseline(null)
    setDrafts([])
    setSaveStatus('idle')
    setPendingDeletion(null)
    setDeletionErrorRecipientId(null)
  }, [tenantId])

  useEffect(() => {
    if (!settingsQuery.data) return
    setBaseline(settingsQuery.data)
    setDrafts(draftsFromResponse(settingsQuery.data))
    setSaveStatus('idle')
  }, [settingsQuery.data])

  const payload = useMemo(() => payloadFromDrafts(drafts), [drafts])
  const payloadValidation = useMemo(
    () => notificationSettingsPayloadSchema.safeParse(payload),
    [payload]
  )
  const recipientErrors = useMemo(() => {
    const errors: RecipientDraftErrors[] = drafts.map(() => ({}))
    if (payloadValidation.success) return errors
    payloadValidation.error.issues.forEach((issue) => {
      const [collection, index, field] = issue.path
      if (
        collection !== 'recipients' ||
        typeof index !== 'number' ||
        typeof field !== 'string' ||
        !(field in (drafts[index] ?? {})) ||
        !errors[index]
      ) {
        return
      }
      const recipientField = field as keyof RecipientDraftErrors
      errors[index][recipientField] ??= issue.message
    })
    return errors
  }, [drafts, payloadValidation])
  const baselinePayload = useMemo(
    () =>
      baseline
        ? payloadFromDrafts(draftsFromResponse(baseline))
        : { recipients: [] },
    [baseline]
  )
  const isDirty = Boolean(
    baseline && JSON.stringify(payload) !== JSON.stringify(baselinePayload)
  )

  const deleteRecipient = useMutation({
    mutationFn: ({
      requestTenantId,
      recipientId,
    }: {
      requestTenantId: string
      recipientId: string
    }) => repository.deleteRecipient(requestTenantId, recipientId),
    onSuccess: (result, variables) => {
      if (activeTenant.current !== variables.requestTenantId) return
      if (result.deletedRecipientId !== variables.recipientId) {
        setDeletionErrorRecipientId(variables.recipientId)
        return
      }
      setBaseline((current) =>
        current
          ? {
              ...current,
              recipients: current.recipients.filter(
                ({ id }) => id !== variables.recipientId
              ),
            }
          : current
      )
      setDrafts((current) =>
        current.filter(({ id }) => id !== variables.recipientId)
      )
      setPendingDeletion(null)
      setDeletionErrorRecipientId(null)
      setSaveStatus('idle')
    },
    onError: (_error, variables) => {
      if (activeTenant.current !== variables.requestTenantId) return
      setDeletionErrorRecipientId(variables.recipientId)
    },
  })
  const deletionPendingForCurrentTenant =
    deleteRecipient.isPending &&
    deleteRecipient.variables.requestTenantId === tenantId

  const saveSettings = useMutation({
    mutationFn: () => {
      if (!payloadValidation.success) {
        throw new Error('Notification settings are invalid')
      }
      return repository.updateSettings(tenantId, payloadValidation.data)
    },
    onSuccess: (saved) => {
      setBaseline(saved)
      setDrafts(draftsFromResponse(saved))
      setSaveStatus('saved')
    },
    onError: () => setSaveStatus('error'),
  })

  function updateDraft(
    draftKey: string,
    update: Partial<NotificationRecipientPayload>
  ) {
    if (saveSettings.isPending) return
    setDrafts((current) =>
      current.map((recipient) =>
        recipient.draftKey === draftKey
          ? { ...recipient, ...update }
          : recipient
      )
    )
    setSaveStatus('idle')
  }

  function addRecipient() {
    if (
      saveSettings.isPending ||
      deletionPendingForCurrentTenant ||
      drafts.length >= 25
    )
      return
    nextDraft.current += 1
    const emailSupported = baseline?.capabilities.email === true
    const smsSupported = baseline?.capabilities.sms === true
    if (!emailSupported && !smsSupported) return
    setDrafts((current) => [
      ...current,
      {
        id: null,
        name: '',
        email: null,
        phone: null,
        enabled: emailSupported || smsSupported,
        emailActive: emailSupported,
        smsActive: !emailSupported && smsSupported,
        appointmentBooked: emailSupported,
        callbackRequested: emailSupported,
        cancellationRequested: emailSupported,
        rescheduleRequested: emailSupported,
        needsAttention: emailSupported,
        appointmentBookedSms: !emailSupported && smsSupported,
        callbackRequestedSms: !emailSupported && smsSupported,
        cancellationRequestedSms: !emailSupported && smsSupported,
        rescheduleRequestedSms: !emailSupported && smsSupported,
        needsAttentionSms: !emailSupported && smsSupported,
        draftKey: `new-${nextDraft.current}`,
      },
    ])
    setSaveStatus('idle')
  }

  function requestRecipientRemoval(recipient: RecipientDraft) {
    if (saveSettings.isPending || deletionPendingForCurrentTenant) return
    setDeletionErrorRecipientId(null)
    if (recipient.id === null) {
      setDrafts((current) =>
        current.filter(({ draftKey }) => draftKey !== recipient.draftKey)
      )
      setSaveStatus('idle')
      return
    }
    setPendingDeletion({
      draftKey: recipient.draftKey,
      recipientId: recipient.id,
      recipientName: recipient.name.trim(),
    })
  }

  function confirmRecipientRemoval() {
    if (!pendingDeletion || deletionPendingForCurrentTenant) return
    deleteRecipient.mutate({
      requestTenantId: tenantId,
      recipientId: pendingDeletion.recipientId,
    })
  }

  return (
    <section className={styles.notificationSettings}>
      <header className={styles.sectionHero}>
        <span className={styles.eyebrow}>Notification settings</span>
        <h2>Who Flio tells, and how</h2>
        <p>
          Route exact operational events to the right staff for{' '}
          <strong>{tenantLabel}</strong>.
        </p>
      </header>

      {settingsQuery.isPending ? (
        <Spinner label="Loading notification settings" />
      ) : null}
      {settingsQuery.isError ? (
        <StatePanel
          kind={
            isNotificationSettingsPermissionError(settingsQuery.error)
              ? 'forbidden'
              : 'error'
          }
          title="Notification settings unavailable"
          description={
            isNotificationSettingsPermissionError(settingsQuery.error)
              ? 'You do not have permission to manage these notification settings.'
              : 'These notification settings could not be loaded.'
          }
        />
      ) : null}

      {baseline ? (
        <div className={styles.notificationGrid}>
          <Card className={styles.recipientCard}>
            <div className={styles.cardHeader}>
              <span>Staff recipients</span>
              <p>Configure each staff recipient and the events they receive.</p>
            </div>

            {!baseline.capabilities.email && !baseline.capabilities.sms ? (
              <div className={styles.emptyRecipients}>
                <BellRing size={22} aria-hidden="true" />
                <div>
                  <strong>No notification channels supported</strong>
                  <p>Recipient configuration is unavailable for this clinic.</p>
                </div>
              </div>
            ) : drafts.length ? (
              <div className={styles.recipientList}>
                {drafts.map((recipient, index) => {
                  const label =
                    recipient.name.trim() || `Recipient ${index + 1}`
                  const errors = recipientErrors[index] ?? {}
                  const nameErrorId = `notification-recipient-${index}-name-error`
                  const emailErrorId = `notification-recipient-${index}-email-error`
                  const phoneErrorId = `notification-recipient-${index}-phone-error`
                  return (
                    <fieldset
                      className={styles.recipientEditor}
                      key={recipient.draftKey}
                      disabled={
                        saveSettings.isPending ||
                        deletionPendingForCurrentTenant
                      }
                    >
                      <legend>{label}</legend>
                      <label>
                        <span>Name</span>
                        <input
                          aria-label="Name"
                          value={recipient.name}
                          maxLength={100}
                          autoComplete="name"
                          aria-invalid={Boolean(errors.name)}
                          aria-describedby={
                            errors.name ? nameErrorId : undefined
                          }
                          onChange={(event) =>
                            updateDraft(recipient.draftKey, {
                              name: event.currentTarget.value,
                            })
                          }
                        />
                        {errors.name ? (
                          <small
                            className={styles.fieldValidationError}
                            id={nameErrorId}
                          >
                            {errors.name}
                          </small>
                        ) : null}
                      </label>
                      {baseline.capabilities.sms ? (
                        <label>
                          <span>Mobile phone</span>
                          <input
                            aria-label="Mobile phone"
                            type="tel"
                            value={recipient.phone ?? ''}
                            maxLength={16}
                            autoComplete="tel"
                            placeholder="+15165550123"
                            aria-invalid={Boolean(errors.phone)}
                            aria-describedby={
                              errors.phone ? phoneErrorId : undefined
                            }
                            onChange={(event) =>
                              updateDraft(recipient.draftKey, {
                                phone: event.currentTarget.value || null,
                              })
                            }
                          />
                          {errors.phone ? (
                            <small
                              className={styles.fieldValidationError}
                              id={phoneErrorId}
                            >
                              {errors.phone}
                            </small>
                          ) : null}
                        </label>
                      ) : null}
                      {baseline.capabilities.email ? (
                        <label>
                          <span>Email address</span>
                          <input
                            aria-label="Email address"
                            type="email"
                            value={recipient.email ?? ''}
                            maxLength={254}
                            autoComplete="email"
                            aria-invalid={Boolean(errors.email)}
                            aria-describedby={
                              errors.email ? emailErrorId : undefined
                            }
                            onChange={(event) =>
                              updateDraft(recipient.draftKey, {
                                email: event.currentTarget.value || null,
                              })
                            }
                          />
                          {errors.email ? (
                            <small
                              className={styles.fieldValidationError}
                              id={emailErrorId}
                            >
                              {errors.email}
                            </small>
                          ) : null}
                        </label>
                      ) : null}
                      <label className={styles.preferenceRow}>
                        <span>
                          <BellRing size={16} aria-hidden="true" />
                          Recipient enabled
                        </span>
                        <input
                          type="checkbox"
                          checked={recipient.enabled}
                          aria-label={`Recipient enabled for ${label}`}
                          onChange={(event) =>
                            updateDraft(recipient.draftKey, {
                              enabled: event.currentTarget.checked,
                            })
                          }
                        />
                      </label>
                      {baseline.capabilities.email ? (
                        <label className={styles.preferenceRow}>
                          <span>
                            <Mail size={16} aria-hidden="true" />
                            Email delivery enabled
                          </span>
                          <input
                            type="checkbox"
                            checked={recipient.emailActive}
                            aria-label={`Email delivery enabled for ${label}`}
                            onChange={(event) =>
                              updateDraft(recipient.draftKey, {
                                emailActive: event.currentTarget.checked,
                              })
                            }
                          />
                        </label>
                      ) : null}
                      {baseline.capabilities.sms ? (
                        <label className={styles.preferenceRow}>
                          <span>
                            <Smartphone size={16} aria-hidden="true" />
                            SMS delivery enabled
                          </span>
                          <input
                            type="checkbox"
                            checked={recipient.smsActive}
                            aria-label={`SMS delivery enabled for ${label}`}
                            onChange={(event) =>
                              updateDraft(recipient.draftKey, {
                                smsActive: event.currentTarget.checked,
                              })
                            }
                          />
                        </label>
                      ) : null}
                      <div
                        className={`${styles.eventMatrix} ${
                          baseline.capabilities.email !==
                          baseline.capabilities.sms
                            ? styles.eventMatrixSingleChannel
                            : ''
                        }`}
                        aria-label={`Notification events for ${label}`}
                      >
                        <span>Event</span>
                        {baseline.capabilities.email ? (
                          <span>Email</span>
                        ) : null}
                        {baseline.capabilities.sms ? <span>SMS</span> : null}
                        {EVENT_PREFERENCES.map(
                          ([emailField, smsField, eventLabel]) => (
                            <div
                              className={styles.eventMatrixRow}
                              key={emailField}
                            >
                              <span>{eventLabel}</span>
                              {baseline.capabilities.email ? (
                                <input
                                  type="checkbox"
                                  checked={recipient[emailField]}
                                  aria-label={`${eventLabel} emails for ${label}`}
                                  onChange={(event) =>
                                    updateDraft(recipient.draftKey, {
                                      [emailField]: event.currentTarget.checked,
                                    })
                                  }
                                />
                              ) : null}
                              {baseline.capabilities.sms ? (
                                <input
                                  type="checkbox"
                                  checked={recipient[smsField]}
                                  aria-label={`${eventLabel} SMS for ${label}`}
                                  onChange={(event) =>
                                    updateDraft(recipient.draftKey, {
                                      [smsField]: event.currentTarget.checked,
                                    })
                                  }
                                />
                              ) : null}
                            </div>
                          )
                        )}
                      </div>
                      {errors.enabled ? (
                        <small className={styles.recipientValidationError}>
                          {errors.enabled}
                        </small>
                      ) : null}
                      <Button
                        type="button"
                        variant="ghost"
                        className={styles.removeRecipientButton}
                        onClick={() => requestRecipientRemoval(recipient)}
                      >
                        <Trash2 size={15} aria-hidden="true" /> Remove {label}
                      </Button>
                      {pendingDeletion?.draftKey === recipient.draftKey ? (
                        <div
                          className={styles.deleteConfirmation}
                          role="alertdialog"
                          aria-label={`Remove ${pendingDeletion.recipientName}?`}
                        >
                          <strong>
                            Remove {pendingDeletion.recipientName}?
                          </strong>
                          <p>
                            This recipient will stop receiving Flio staff
                            notifications. Any notification settings stored for
                            this recipient will also be removed.
                          </p>
                          <div>
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={() => setPendingDeletion(null)}
                              disabled={deletionPendingForCurrentTenant}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              className={styles.confirmDeleteButton}
                              onClick={confirmRecipientRemoval}
                              disabled={deletionPendingForCurrentTenant}
                            >
                              {deletionPendingForCurrentTenant
                                ? 'Removing…'
                                : 'Remove recipient'}
                            </Button>
                          </div>
                        </div>
                      ) : null}
                      {deletionErrorRecipientId !== null &&
                      recipient.id === deletionErrorRecipientId ? (
                        <small
                          className={styles.recipientValidationError}
                          role="alert"
                        >
                          This recipient could not be removed. It remains
                          configured.
                        </small>
                      ) : null}
                    </fieldset>
                  )
                })}
              </div>
            ) : (
              <div className={styles.emptyRecipients}>
                <Mail size={22} aria-hidden="true" />
                <div>
                  <strong>No staff recipients configured</strong>
                  <p>Add a recipient to begin receiving staff notifications.</p>
                </div>
              </div>
            )}

            {baseline.capabilities.email || baseline.capabilities.sms ? (
              <Button
                type="button"
                variant="secondary"
                onClick={addRecipient}
                disabled={
                  saveSettings.isPending ||
                  deletionPendingForCurrentTenant ||
                  drafts.length >= 25
                }
              >
                <Plus size={16} aria-hidden="true" /> Add recipient
              </Button>
            ) : null}
          </Card>

          <Card className={styles.channelCard}>
            <div className={styles.cardHeader}>
              <span>Delivery channels</span>
              <p>Capabilities currently available to clinic staff.</p>
            </div>
            <div className={styles.channelStatus}>
              <Mail size={18} aria-hidden="true" />
              <div>
                <strong>
                  {baseline.capabilities.email
                    ? 'Email supported'
                    : 'Email unsupported'}
                </strong>
                <p>
                  {baseline.capabilities.email
                    ? 'Provider acceptance is recorded by the Notification service.'
                    : 'Email recipient configuration is not available for this clinic.'}
                </p>
              </div>
            </div>
            <div className={styles.channelStatus}>
              <Smartphone size={18} aria-hidden="true" />
              <div>
                <strong>
                  {baseline.capabilities.sms
                    ? 'Staff SMS supported'
                    : 'Staff SMS unsupported'}
                </strong>
                <p>
                  {baseline.capabilities.sms
                    ? 'Internal alerts use the clinic’s reviewed Flio number and do not create patient conversations.'
                    : 'Staff SMS recipient configuration is not available for this clinic.'}
                </p>
              </div>
            </div>
          </Card>

          {!payloadValidation.success && isDirty ? (
            <p className={styles.formError} role="alert">
              Review the highlighted recipient fields before saving.
            </p>
          ) : null}
          {saveStatus === 'saved' ? (
            <p className={styles.saveSuccess} role="status">
              <Check size={16} aria-hidden="true" /> Notification settings
              saved.
            </p>
          ) : null}
          {saveStatus === 'error' ? (
            <p className={styles.formError} role="alert">
              Notification settings could not be saved. Your edits are still
              shown.
            </p>
          ) : null}
          {baseline.capabilities.email || baseline.capabilities.sms ? (
            <div className={styles.settingsActions}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  if (!baseline) return
                  setDrafts(draftsFromResponse(baseline))
                  setSaveStatus('idle')
                }}
                disabled={
                  !isDirty ||
                  saveSettings.isPending ||
                  deletionPendingForCurrentTenant
                }
              >
                <RotateCcw size={16} aria-hidden="true" /> Cancel changes
              </Button>
              <Button
                type="button"
                onClick={() => saveSettings.mutate()}
                disabled={
                  !isDirty ||
                  !payloadValidation.success ||
                  saveSettings.isPending ||
                  deletionPendingForCurrentTenant
                }
              >
                <Save size={16} aria-hidden="true" />
                {saveSettings.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}
